import { DATA_BASE_URL, WS_HUB_URL, applyStreamEnvelope, indexRowsByPublisherId, validateCategoryManifest, validateReleaseManifest, verifyCanonicalJsonArtifact } from './liveOddsVnextContract.js'
import { fetchMatchingR2Category, fetchR2Pointer } from './liveOddsR2ClientV1.js'
const CATEGORY_MANIFEST_NAME = 'crossbook_allbets_manifest.json'
async function fetchJson(url, fetchImpl, signal) {
  if (typeof fetchImpl !== 'function') throw new TypeError('fetchImpl must be a function')
  const response = await fetchImpl(url, { cache: 'no-store', signal })
  if (!response || response.ok !== true || typeof response.json !== 'function') {
    const status = response?.status ?? 'unavailable'
    throw new Error(`live odds request failed (HTTP ${status})`)
  }
  return response.json()
}
function safeCategorySlug(slug) {
  if (typeof slug !== 'string' || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    throw new TypeError('category slug is unsafe')
  }
  return slug
}

function categorySnapshotUrl(slug) {
  return `${DATA_BASE_URL}/cat_${safeCategorySlug(slug)}.json`
}

function sameInstant(left, right) {
  const leftValue = Date.parse(left)
  const rightValue = Date.parse(right)
  return Number.isFinite(leftValue) && leftValue === rightValue
}

function snapshotAlreadyLoaded(envelope, state) {
  if (state.deliveryMode === 'r2' && state.selectedCategory !== 'game-lines'
      && envelope.release_id === state.releaseId
      && sameInstant(envelope.captured_utc, state.capturedUtc)) return true
  if (!String(envelope.release_id).startsWith('legacy-') || envelope.row_count !== state.rows.length) return false
  if (!sameInstant(envelope.captured_utc, state.capturedUtc)) return false
  try { return new URL(envelope.snapshot_url).pathname.endsWith(`/cat_${safeCategorySlug(state.selectedCategory)}.json`) }
  catch { return false }
}
function validateShard(value, slug) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError('category shard must be an object')
  }
  if (value.slug !== slug) throw new TypeError(`category shard slug mismatch: ${value.slug || 'missing'}`)
  if (typeof value.category !== 'string' || value.category.trim() === '') {
    throw new TypeError('category shard must name its category')
  }
  if (!Array.isArray(value.rows) || value.rows.some((row) => row === null || typeof row !== 'object')) {
    throw new TypeError('category shard rows must be objects')
  }
  return value
}
export async function fetchReleaseManifest(fetchImpl = fetch, signal) {
  const value = await fetchJson(`${DATA_BASE_URL}/release_manifest.json`, fetchImpl, signal)
  return validateReleaseManifest(value)
}
export async function fetchCategoryManifest(fetchImpl = fetch, signal) {
  const value = await fetchJson(`${DATA_BASE_URL}/${CATEGORY_MANIFEST_NAME}`, fetchImpl, signal)
  return validateCategoryManifest(value)
}
export async function fetchCategoryShard(slug, fetchImpl = fetch, signal) {
  const safeSlug = safeCategorySlug(slug)
  const value = await fetchJson(`${DATA_BASE_URL}/cat_${safeSlug}.json`, fetchImpl, signal)
  return validateShard(value, safeSlug)
}
function resolveBootstrapMetadata(releaseManifest, categoryManifest, shard, categorySlug) {
  const category = categoryManifest.categories.find((item) => item.slug === categorySlug)
  if (!category) throw new Error(`category is absent from manifest: ${categorySlug}`)
  if (category.n_rows !== shard.rows.length) throw new Error('category shard row count mismatch')
  if (category.category !== shard.category) throw new Error('category shard name mismatch')
  if (!sameInstant(categoryManifest.captured_utc, releaseManifest.captured_utc)) {
    throw new Error('release and category manifests describe different captures')
  }
  const artifactName = `cat_${categorySlug}.json`
  const artifact = releaseManifest.artifacts[artifactName] ?? null
  const categoryManifestArtifact = releaseManifest.artifacts[CATEGORY_MANIFEST_NAME] ?? null
  if (artifact === null || categoryManifestArtifact === null) {
    throw new Error(`selected category is absent from the signed release: ${categorySlug}`)
  }
  return {
    artifactName,
    artifact,
    categoryManifestArtifact,
    category,
    releaseId: releaseManifest.release_id,
    capturedUtc: releaseManifest.captured_utc,
    unverifiedArtifact: false,
  }
}

export async function bootstrapLiveOdds({
  categorySlug = 'game-lines',
  fetchImpl = fetch,
  digestImpl,
  cryptoImpl = globalThis.crypto,
  signal,
} = {}) {
  const safeSlug = safeCategorySlug(categorySlug)
  const [releaseManifest, categoryManifest, r2Pointer] = await Promise.all([
    fetchReleaseManifest(fetchImpl, signal),
    fetchCategoryManifest(fetchImpl, signal),
    fetchR2Pointer(fetchImpl, signal),
  ])
  const categoryManifestArtifact = releaseManifest.artifacts[CATEGORY_MANIFEST_NAME] ?? null
  if (categoryManifestArtifact === null) throw new Error('category manifest is absent from signed release')
  const categoryManifestVerificationPromise = verifyCanonicalJsonArtifact(
    categoryManifest,
    categoryManifestArtifact,
    { digestImpl, cryptoImpl },
  )
  const r2Result = await fetchMatchingR2Category({
      pointer: r2Pointer,
      releaseManifest,
      categoryManifest,
      categorySlug: safeSlug,
      fetchImpl,
      digestImpl,
      cryptoImpl,
      signal,
  })
  if (r2Result !== null) {
    return {
      releaseManifest: r2Result.releaseManifest,
      categoryManifest,
      r2ReleaseManifest: r2Result.release,
      shard: r2Result.shard,
      rows: r2Result.rows,
      artifactVerification: r2Result.artifactVerification,
      categoryManifestVerification: await categoryManifestVerificationPromise,
      artifactName: `r2:${safeSlug}`,
      artifact: {
        sha256: r2Result.category.source_sha256,
        canonical_bytes: r2Result.category.source_canonical_bytes,
      },
      categoryManifestArtifact,
      category: r2Result.manifestCategory,
      releaseId: releaseManifest.release_id,
      capturedUtc: releaseManifest.captured_utc,
      unverifiedArtifact: false,
      deliveryMode: 'r2',
    }
  }
  const shard = await fetchCategoryShard(safeSlug, fetchImpl, signal)
  const metadata = resolveBootstrapMetadata(releaseManifest, categoryManifest, shard, safeSlug)
  const [artifactVerification, categoryManifestVerification] = await Promise.all([
    verifyCanonicalJsonArtifact(shard, metadata.artifact, { digestImpl, cryptoImpl }),
    categoryManifestVerificationPromise,
  ])
  return {
    releaseManifest,
    categoryManifest,
    shard,
    rows: shard.rows,
    artifactVerification,
    categoryManifestVerification,
    ...metadata,
    deliveryMode: 'v1',
  }
}
function streamUrl(sequence) {
  const safeSequence = Number.isSafeInteger(sequence) && sequence >= 0 ? sequence : 0
  return `${WS_HUB_URL}?since=${encodeURIComponent(String(safeSequence))}`
}
function bindSocket(socket, callbacks, updateSequence) {
  const { onEnvelope, onStatus, onError } = callbacks
  socket.addEventListener('open', () => onStatus?.({ state: 'open', url: socket.url }))
  socket.addEventListener('message', (event) => {
    try {
      const envelope = JSON.parse(event.data)
      onEnvelope?.(envelope)
      if (Number.isSafeInteger(envelope?.sequence) && envelope.sequence >= 0) {
        updateSequence(envelope.sequence)
      }
    } catch (error) {
      onStatus?.({ state: 'message_error', error })
      onError?.(error)
    }
  })
  socket.addEventListener('error', (event) => onError?.(event))
  socket.addEventListener('close', (event) => onStatus?.({
    state: 'closed',
    code: event.code,
    reason: event.reason,
  }))
}
export function createLiveOddsStream({
  onEnvelope,
  onStatus,
  onError,
  lastSequence = 0,
  WebSocketImpl = WebSocket,
} = {}) {
  if (typeof WebSocketImpl !== 'function') throw new TypeError('WebSocketImpl must be a constructor')
  let latestSequence = Number.isSafeInteger(lastSequence) && lastSequence >= 0 ? lastSequence : 0
  let socket = null
  const connect = (sequence) => {
    latestSequence = Number.isSafeInteger(sequence) && sequence >= 0 ? sequence : latestSequence
    socket = new WebSocketImpl(streamUrl(latestSequence))
    bindSocket(socket, { onEnvelope, onStatus, onError }, (value) => { latestSequence = value })
    return socket
  }
  connect(latestSequence)
  return {
    get socket() { return socket },
    get lastSequence() { return latestSequence },
    get reconnectUrl() { return streamUrl(latestSequence) },
    close(code = 1000, reason = 'client close') { socket?.close(code, reason) },
    reconnect(sequence = latestSequence) {
      socket?.close(1000, 'client reconnect')
      return connect(sequence)
    },
  }
}
export function fetchLiveOddsCategory(slug, options = {}) {
  return bootstrapLiveOdds({ ...options, categorySlug: slug })
}
async function fetchSnapshot(url, slug, fetchImpl, signal) {
  const parsed = new URL(url)
  if (parsed.protocol !== 'https:') throw new TypeError('snapshot URL must use HTTPS')
  return validateShard(await fetchJson(parsed.href, fetchImpl, signal), slug)
}
function storeInitialState(categorySlug) {
  return {
    rows: [],
    releaseManifest: null,
    categoryManifest: null,
    r2ReleaseManifest: null,
    selectedCategory: categorySlug,
    releaseId: null,
    capturedUtc: null,
    sequence: 0,
    httpStatus: 'idle',
    streamStatus: 'idle',
    degradedReason: null,
    error: null,
    lastMessageUtc: null,
    unverifiedArtifact: false,
    artifactVerification: null,
    categoryManifestVerification: null,
    deliveryMode: null,
  }
}
class LiveOddsVnextStore {
  constructor(options = {}) {
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch
    this.WebSocketImpl = options.WebSocketImpl ?? globalThis.WebSocket
    this.digestImpl = options.digestImpl
    this.cryptoImpl = options.cryptoImpl ?? globalThis.crypto
    this.identityConcurrency = options.identityConcurrency ?? 32
    this.setTimeoutImpl = options.setTimeoutImpl ?? ((callback, delay) => globalThis.setTimeout(callback, delay))
    this.clearTimeoutImpl = options.clearTimeoutImpl ?? ((timer) => globalThis.clearTimeout(timer))
    this.AbortControllerImpl = options.AbortControllerImpl ?? globalThis.AbortController
    this.now = options.now ?? (() => new Date().toISOString())
    this.delays = options.reconnectDelays ?? [250, 1000, 5000, 15000]
    this.state = storeInitialState(options.categorySlug ?? 'game-lines')
    this.reducerState = { sequence: 0, releaseId: null, offers: {}, degradedReason: null }
    this.listeners = new Set()
    this.active = false
    this.generation = 0
    this.reconnectAttempt = 0
    this.timer = null
    this.stream = null
    this.abortController = null
  }
  getState() { return this.state }
  subscribe(listener) {
    if (typeof listener !== 'function') throw new TypeError('listener must be a function')
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
  update(patch) {
    this.state = { ...this.state, ...patch }
    for (const listener of this.listeners) listener(this.state)
  }
  start() {
    if (this.active) return Promise.resolve(this.state)
    this.active = true
    return this.restart(false)
  }
  stop() {
    this.active = false
    this.generation += 1
    this.cancelTimer()
    this.abortController?.abort()
    this.abortController = null
    this.stream?.close(1000, 'store stop')
    this.stream = null
    this.update({ streamStatus: 'stopped' })
  }
  async selectCategory(slug) {
    const selectedCategory = safeCategorySlug(slug)
    if (selectedCategory === this.state.selectedCategory) return this.state
    this.update({
      selectedCategory, rows: [], releaseManifest: null, r2ReleaseManifest: null,
      releaseId: null, capturedUtc: null,
      httpStatus: 'idle', error: null, unverifiedArtifact: false,
      artifactVerification: null, categoryManifestVerification: null, deliveryMode: null,
    })
    if (!this.active) return this.state
    return this.restart(true)
  }
  reconnect() {
    if (!this.active) return this.start()
    return this.restart(true)
  }
  cancelTimer() {
    if (this.timer !== null) this.clearTimeoutImpl(this.timer)
    this.timer = null
  }
  restart(resetAttempts) {
    if (resetAttempts) this.reconnectAttempt = 0
    this.cancelTimer()
    const generation = ++this.generation
    this.abortController?.abort()
    this.abortController = new this.AbortControllerImpl()
    const categorySlug = this.state.selectedCategory
    this.stream?.close(1000, 'store rebootstrap')
    this.stream = null
    return this.bootstrap(generation, categorySlug, this.abortController.signal)
  }
  isCurrent(generation, categorySlug, signal) {
    return this.active && generation === this.generation
      && categorySlug === this.state.selectedCategory && !signal?.aborted
  }
  async bootstrap(generation, categorySlug, signal) {
    this.update({ httpStatus: 'loading', streamStatus: 'idle', error: null })
    try {
      const result = await fetchLiveOddsCategory(categorySlug, {
        fetchImpl: this.fetchImpl,
        digestImpl: this.digestImpl,
        cryptoImpl: this.cryptoImpl,
        signal,
      })
      if (!this.isCurrent(generation, categorySlug, signal)) return this.state
      const offers = await indexRowsByPublisherId(result.rows, {
        digestImpl: this.digestImpl,
        cryptoImpl: this.cryptoImpl,
        identityConcurrency: this.identityConcurrency,
      })
      if (!this.isCurrent(generation, categorySlug, signal)) return this.state
      const degradedReason = result.unverifiedArtifact ? 'unverified_artifact' : null
      this.reducerState = { sequence: 0, releaseId: result.releaseId, offers, degradedReason }
      this.update({
        rows: result.rows,
        releaseManifest: result.releaseManifest,
        categoryManifest: result.categoryManifest,
        r2ReleaseManifest: result.r2ReleaseManifest ?? null,
        releaseId: result.releaseId,
        capturedUtc: result.capturedUtc,
        sequence: 0,
        httpStatus: 'ready',
        streamStatus: degradedReason ? 'degraded' : 'connecting',
        degradedReason,
        unverifiedArtifact: result.unverifiedArtifact,
        artifactVerification: result.artifactVerification,
        categoryManifestVerification: result.categoryManifestVerification,
        deliveryMode: result.deliveryMode,
      })
      if (this.isCurrent(generation, categorySlug, signal)) this.connect(generation)
    } catch (error) {
      if (error?.name !== 'AbortError' && this.isCurrent(generation, categorySlug, signal)) {
        this.fail(error, 'bootstrap_failed', true)
      }
    }
    return this.state
  }
  connect(generation) {
    this.stream = createLiveOddsStream({
      lastSequence: this.state.sequence,
      WebSocketImpl: this.WebSocketImpl,
      onEnvelope: (value) => {
        if (this.active && generation === this.generation) this.handleEnvelope(value, generation)
      },
      onStatus: (value) => {
        if (this.active && generation === this.generation) this.handleStatus(value)
      },
      onError: (error) => {
        if (this.active && generation === this.generation) this.fail(error, 'stream_error', true)
      },
    })
  }
  handleEnvelope(envelope, generation) {
    const legacyRelease = String(envelope?.release_id || '').startsWith('legacy-')
    const verifiedHttpRelease = Boolean(this.state.artifactVerification?.sha256)
      && this.state.releaseId && !String(this.state.releaseId).startsWith('legacy-')
    if (legacyRelease && verifiedHttpRelease) {
      this.update({
        streamStatus: 'degraded',
        degradedReason: `legacy_stream_rejected:${envelope.release_id}`,
        error: null,
        lastMessageUtc: this.now(),
      })
      return
    }
    if (this.state.selectedCategory !== 'game-lines' && envelope.type === 'delta') {
      void this.restart(false)
      return
    }
    if (this.state.selectedCategory !== 'game-lines' && envelope.type === 'snapshot_ref') {
      if (this.state.releaseId && envelope.release_id !== this.state.releaseId) {
        void this.restart(false)
        return
      }
      if (this.state.deliveryMode !== 'r2') {
        envelope = { ...envelope, snapshot_url: categorySnapshotUrl(this.state.selectedCategory) }
      }
    }
    if (envelope.type === 'snapshot_ref' && !String(envelope.release_id).startsWith('legacy-')
      && this.state.releaseId && envelope.release_id !== this.state.releaseId) {
      throw new RangeError('snapshot release mismatch')
    }
    let reduced = applyStreamEnvelope(this.reducerState, envelope)
    if (envelope.type === 'snapshot_ref' && snapshotAlreadyLoaded(envelope, this.state)) {
      reduced = {
        ...reduced,
        needsSnapshot: false,
        expectedRowCount: this.state.rows.length,
        status: 'open',
      }
    }
    this.reducerState = reduced
    const degradedReason = reduced.degradedReason
      || (this.state.unverifiedArtifact ? 'unverified_artifact' : null)
    this.update({
      rows: envelope.type === 'delta' ? Object.values(reduced.offers) : this.state.rows,
      releaseId: reduced.releaseId ?? this.state.releaseId,
      capturedUtc: reduced.capturedUtc ?? this.state.capturedUtc,
      sequence: reduced.sequence,
      httpStatus: reduced.needsSnapshot ? 'loading' : this.state.httpStatus,
      streamStatus: degradedReason ? 'degraded' : reduced.status,
      degradedReason,
      error: null,
      lastMessageUtc: this.now(),
    })
    if (reduced.needsSnapshot && envelope.type === 'snapshot_ref') {
      const categorySlug = this.state.selectedCategory
      const signal = this.abortController?.signal
      void this.reloadSnapshot(envelope, generation, categorySlug, signal).catch((error) => {
        if (error?.name === 'AbortError') return
        if (this.active && generation === this.generation) this.fail(error, 'snapshot_failed', true)
      })
    }
  }
  async reloadSnapshot(envelope, generation, categorySlug, signal) {
    const shard = await fetchSnapshot(envelope.snapshot_url, categorySlug, this.fetchImpl, signal)
    if (!this.isCurrent(generation, categorySlug, signal)) return
    const artifactName = `cat_${categorySlug}.json`
    const artifact = this.state.releaseManifest?.artifacts?.[artifactName] ?? null
    const sameRelease = envelope.release_id === this.state.releaseManifest?.release_id
    if (artifact && sameRelease) {
      await verifyCanonicalJsonArtifact(shard, artifact, {
        digestImpl: this.digestImpl,
        cryptoImpl: this.cryptoImpl,
      })
    }
    if (!this.isCurrent(generation, categorySlug, signal)) return
    const offers = await indexRowsByPublisherId(shard.rows, {
      digestImpl: this.digestImpl,
      cryptoImpl: this.cryptoImpl,
      identityConcurrency: this.identityConcurrency,
    })
    if (!this.isCurrent(generation, categorySlug, signal)) return
    this.reducerState = { ...this.reducerState, offers, needsSnapshot: false }
    const unverifiedArtifact = this.state.unverifiedArtifact || !artifact || !sameRelease
    const degradedReason = this.reducerState.degradedReason
      || (unverifiedArtifact ? 'unverified_artifact' : null)
    this.update({
      rows: shard.rows,
      httpStatus: 'ready',
      streamStatus: degradedReason ? 'degraded' : 'open',
      degradedReason,
      unverifiedArtifact,
    })
  }
  handleStatus(status) {
    if (status.state === 'open') {
      this.reconnectAttempt = 0
      this.update({ streamStatus: this.state.degradedReason ? 'degraded' : 'open' })
    } else if (status.state === 'closed') {
      this.fail(new Error(`stream closed (${status.code ?? 'unknown'})`), 'stream_closed', true)
    }
  }
  fail(value, reason, recover) {
    const error = value instanceof Error ? value : new Error(String(value?.message ?? value ?? reason))
    const httpStatus = /^(bootstrap|snapshot)_failed$/.test(reason) ? 'error' : this.state.httpStatus
    this.update({ httpStatus, streamStatus: 'degraded', degradedReason: `${reason}:${error.message}`, error: error.message })
    if (recover) this.scheduleRecovery()
  }
  scheduleRecovery() {
    if (!this.active || this.timer !== null) return
    if (this.reconnectAttempt >= this.delays.length) {
      this.update({ degradedReason: 'reconnect_exhausted', streamStatus: 'degraded' })
      return
    }
    const delay = this.delays[this.reconnectAttempt++]
    this.update({ streamStatus: 'reconnecting' })
    this.timer = this.setTimeoutImpl(() => {
      this.timer = null
      if (this.active) void this.restart(false)
    }, delay)
  }
}
export function createLiveOddsVnextStore(options = {}) {
  const store = new LiveOddsVnextStore(options)
  return {
    getState: () => store.getState(),
    subscribe: (listener) => store.subscribe(listener),
    start: () => store.start(),
    stop: () => store.stop(),
    selectCategory: (slug) => store.selectCategory(slug),
    reconnect: () => store.reconnect(),
  }
}
